import {
  QUALITY_EVIDENCE_MAGIC,
  QUALITY_EVIDENCE_MAX_MANIFEST_BYTES,
} from '@/features/uploads/config/qualityEvidence';

interface Segment {
  offset: string;
  length: number;
}

interface ExtractionDone {
  type: 'done';
  originalFileSize: string;
  segments: Segment[];
  payload: ArrayBuffer;
}

interface ExtractionError {
  type: 'error';
  message: string;
}

type ExtractionMessage = ExtractionDone | ExtractionError;

export async function buildQualityEvidence(file: File): Promise<ArrayBuffer> {
  const worker = new Worker(new URL('../workers/qualityEvidence.worker.ts', import.meta.url));
  try {
    const extracted = await new Promise<ExtractionDone>((resolve, reject) => {
      worker.onmessage = (event: MessageEvent<ExtractionMessage>) => {
        if (event.data.type === 'done') resolve(event.data);
        else reject(new Error(event.data.message));
      };
      worker.onerror = () => reject(new Error('Quality evidence worker failed'));
      worker.postMessage({ type: 'extract', file });
    });
    return packQualityEvidence(extracted);
  } finally {
    worker.terminate();
  }
}

function packQualityEvidence(extracted: Omit<ExtractionDone, 'type'>): ArrayBuffer {
  const manifest = new TextEncoder().encode(
    JSON.stringify({
      evidenceVersion: 1,
      extractor: 'mp4box',
      extractorVersion: '1',
      originalFileSize: extracted.originalFileSize,
      container: 'iso-bmff',
      segments: extracted.segments,
    }),
  );
  if (manifest.length === 0 || manifest.length > QUALITY_EVIDENCE_MAX_MANIFEST_BYTES) {
    throw new Error('Quality evidence manifest is too large');
  }
  const envelope = new Uint8Array(8 + manifest.length + extracted.payload.byteLength);
  envelope.set(QUALITY_EVIDENCE_MAGIC, 0);
  new DataView(envelope.buffer).setUint32(4, manifest.length);
  envelope.set(manifest, 8);
  envelope.set(new Uint8Array(extracted.payload), 8 + manifest.length);
  return envelope.buffer;
}
